import { useEffect, useRef, useState } from 'react';
import api from '../../api/axios';

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|svg)$/i;
const fmtSize = b => b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

export default function MediaManager() {
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState('');
  const [files, setFiles] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [newFolder, setNewFolder] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const fileRef = useRef(null);

  const loadFolders = () => {
    setLoadingFolders(true);
    api.get('/media/folders')
      .then(r => {
        const f = r.data || [];
        setFolders(f);
        if (f.length && !activeFolder) setActiveFolder(f[0]);
      })
      .finally(() => setLoadingFolders(false));
  };

  const loadFiles = (prefix) => {
    if (!prefix) return;
    setLoadingFiles(true);
    api.get(`/media/files?prefix=${encodeURIComponent(prefix)}`)
      .then(r => setFiles(r.data || []))
      .finally(() => setLoadingFiles(false));
  };

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => { if (activeFolder) loadFiles(activeFolder); }, [activeFolder]);

  const selectFolder = (f) => { setActiveFolder(f); setError(''); };

  const upload = async (e) => {
    e.preventDefault();
    const selectedFiles = Array.from(fileRef.current?.files || []);
    const folder = newFolder.trim() || activeFolder;
    if (!selectedFiles.length || !folder) return setError('Select at least one file and a folder.');
    setError('');
    setUploadProgress({ done: 0, total: selectedFiles.length });

    let done = 0;
    const errors = [];

    await Promise.all(selectedFiles.map(async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await api.post(`/media/upload?folder=${encodeURIComponent(folder)}`, fd);
      } catch (err) {
        errors.push(`${file.name}: ${err.response?.data?.error || 'failed'}`);
      } finally {
        done++;
        setUploadProgress({ done, total: selectedFiles.length });
      }
    }));

    fileRef.current.value = '';
    setNewFolder('');
    setUploadProgress(null);

    if (errors.length) setError(errors.join(' | '));

    const targetFolder = newFolder.trim() || folder;
    if (newFolder.trim() && !folders.includes(newFolder.trim())) {
      loadFolders();
      setActiveFolder(newFolder.trim());
    } else {
      loadFiles(targetFolder);
    }
  };

  const deleteFile = async (key) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await api.delete(`/media/file?key=${encodeURIComponent(key)}`);
      setFiles(f => f.filter(x => x.key !== key));
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  return (
    <div>
      <div className="mb-4">
        <h4 className="fw-bold mb-0">Media Manager</h4>
        <p className="text-muted small mb-0">Upload and manage images and files. Images are automatically resized for web.</p>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <h5 className="fw-bold mb-3">Upload Files</h5>
          <form onSubmit={upload}>
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <label className="form-label small">Files * <span className="text-muted fw-normal">(select multiple)</span></label>
                <input type="file" ref={fileRef} multiple required className="form-control" />
              </div>
              <div className="col-md-4">
                <label className="form-label small">Folder</label>
                <input
                  type="text"
                  value={newFolder}
                  onChange={e => setNewFolder(e.target.value)}
                  placeholder={activeFolder || 'e.g. events'}
                  className="form-control"
                />
                <small className="text-muted">Leave blank to upload to selected folder</small>
              </div>
              <div className="col-md-3">
                <button type="submit" disabled={!!uploadProgress} className="btn btn-primary rounded-pill px-4 w-100">
                  {uploadProgress
                    ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…`
                    : <><i className="fas fa-upload me-2"></i>Upload</>}
                </button>
              </div>
            </div>
            {uploadProgress && (
              <div className="mt-3">
                <div className="progress" style={{ height: '6px' }}>
                  <div
                    className="progress-bar"
                    style={{ width: `${Math.round((uploadProgress.done / uploadProgress.total) * 100)}%`, transition: 'width 0.2s' }}
                  ></div>
                </div>
                <small className="text-muted mt-1 d-block">
                  {uploadProgress.done} of {uploadProgress.total} file{uploadProgress.total !== 1 ? 's' : ''} uploaded
                </small>
              </div>
            )}
          </form>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body p-3">
              <h6 className="fw-bold mb-3">Folders</h6>
              {loadingFolders ? (
                <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-primary"></div></div>
              ) : folders.length === 0 ? (
                <p className="text-muted small">No folders yet. Upload files to create one.</p>
              ) : (
                <ul className="list-unstyled mb-0">
                  {folders.map(f => (
                    <li key={f}>
                      <button
                        onClick={() => selectFolder(f)}
                        className={`btn btn-sm w-100 text-start rounded px-3 py-2 mb-1 ${activeFolder === f ? 'btn-primary' : 'btn-light'}`}
                      >
                        <i className="fas fa-folder me-2"></i>{f}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-9">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <h6 className="fw-bold mb-3">
                {activeFolder ? `Files in "${activeFolder}"` : 'Select a folder'}
                {files.length > 0 && <span className="text-muted fw-normal ms-2 small">({files.length} files)</span>}
              </h6>
              {loadingFiles ? (
                <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
              ) : !activeFolder ? (
                <p className="text-muted text-center py-4">Select a folder to view files.</p>
              ) : files.length === 0 ? (
                <p className="text-muted text-center py-4">No files in this folder.</p>
              ) : (
                <div className="row g-3">
                  {files.map(f => (
                    <div key={f.key} className="col-sm-6 col-md-4 col-lg-3">
                      <div className="border rounded overflow-hidden position-relative" style={{ background: '#f8f9fa' }}>
                        {IMAGE_EXT.test(f.name) ? (
                          <img
                            src={f.url}
                            alt={f.name}
                            className="w-100"
                            style={{ height: '130px', objectFit: 'cover', display: 'block' }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center" style={{ height: '130px' }}>
                            <i className="fas fa-file-alt fa-3x text-muted"></i>
                          </div>
                        )}
                        <div className="p-2">
                          <div className="text-truncate small fw-semibold" title={f.name}>{f.name}</div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>{fmtSize(f.size)}</div>
                          <div className="d-flex gap-1 mt-2">
                            <button
                              className={`btn btn-sm py-0 px-2 ${copied === f.url ? 'btn-success' : 'btn-outline-secondary'}`}
                              style={{ fontSize: '11px' }}
                              onClick={() => copyUrl(f.url)}
                              title="Copy URL"
                            >
                              <i className={copied === f.url ? 'fas fa-check' : 'fas fa-copy'}></i>
                            </button>
                            <a href={f.url} target="_blank" rel="noreferrer" className="btn btn-sm py-0 px-2 btn-outline-primary" style={{ fontSize: '11px' }} title="Open">
                              <i className="fas fa-external-link-alt"></i>
                            </a>
                            <button className="btn btn-sm py-0 px-2 btn-outline-danger ms-auto" style={{ fontSize: '11px' }} onClick={() => deleteFile(f.key)} title="Delete">
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
